import { useParams } from "react-router";
import { MaintainCompanyConfigForm } from "../components";
import { useCompanyConfigOverview } from "../hooks";

interface MaintainCompanyConfigScreenProps {
  presentation?: "legacy" | "redesign";
  cataloguePath?: string;
}

/**
 * Create (`/onboarding/new`) or edit (`/onboarding/:configCode`) a company
 * configuration. In edit mode the current published version is loaded before
 * rendering the form so it can be pre-populated.
 */
export function MaintainCompanyConfigScreen({
  presentation = "redesign",
  cataloguePath = "/onboarding",
}: MaintainCompanyConfigScreenProps) {
  const { configCode } = useParams<{ configCode: string }>();
  const isEdit = Boolean(configCode);

  const { data, isLoading, isError } = useCompanyConfigOverview(configCode);

  if (!isEdit) {
    return (
      <MaintainCompanyConfigForm
        mode="create"
        presentation={presentation}
        cataloguePath={cataloguePath}
      />
    );
  }

  if (isLoading) {
    return <p role="status">Loading company configuration…</p>;
  }

  if (isError || !data) {
    return (
      <div role="alert">
        Could not load company configuration &quot;{configCode}&quot;.
      </div>
    );
  }

  // Keyed on config_code+version so the form fully remounts (and re-seeds its
  // internal state from `initial`) whenever the fetched overview refers to a
  // different version — e.g. a stale cached version is replaced by a fresh
  // background refetch after a retire. useControlled only reads its initial
  // value once per mount, so without this key it would keep displaying the
  // stale version's data even after `data` updates.
  //
  // Trade-off: if the version changes mid-edit (e.g. a concurrent publish
  // plus this query's default `staleTime: 0` triggering a background
  // refetch), the remount silently discards in-progress edits. Accepted as a
  // narrow, low-likelihood edge case rather than a bug.
  return (
    <MaintainCompanyConfigForm
      key={`${data.config_code}-${data.version}`}
      mode="edit"
      initial={data}
      presentation={presentation}
      cataloguePath={cataloguePath}
    />
  );
}
